import { useCallback, useEffect, useState } from "react";

/**
 * Sidebar collapse state hook with three responsibilities:
 *
 * 1. Persist user choice in localStorage so the sidebar feels stable
 *    across reloads.
 * 2. Force-collapse on small viewports (<768px) so the layout never
 *    fights mobile/tablet portrait. We also reset on every resize so a
 *    landscape→portrait flip in iPad Safari doesn't keep the previous
 *    width or font scale (bug user reported).
 * 3. Provide a stable ``toggle`` setter the UI can hand to a button.
 *
 * The hook returns ``[collapsed, toggle, setCollapsed]`` — the second
 * is a no-arg function that flips the value.
 */
export const SIDEBAR_KEY = "elswitch_sidebar_collapsed";
const SMALL_VP = 768;

export function useSidebarCollapsed(defaultValue = false) {
  // Initialise from storage on the very first render so we never paint
  // the wrong width.
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_KEY);
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch {
      /* SSR / privacy mode — fall through */
    }
    return defaultValue;
  });

  // Persist updates back to storage.
  const setCollapsed = useCallback((next) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Phase M — viewport reset. On every resize / orientation change we
  // re-read the stored preference so the sidebar can never be stuck at a
  // mismatched width after a landscape→portrait flip on iPad. Below the
  // mobile breakpoint we always render the off-canvas Sheet anyway, so we
  // do NOT force collapsed=true here; we just refresh from storage.
  useEffect(() => {
    const onResize = () => {
      try {
        const stored = localStorage.getItem(SIDEBAR_KEY);
        if (stored === "1") setCollapsedState(true);
        else if (stored === "0") setCollapsedState(false);
      } catch {
        /* ignore */
      }
      // Force a reflow so any stale orientation-locked metrics (font
      // sizes, computed widths) get recomputed.
      // eslint-disable-next-line no-unused-expressions
      document.documentElement.offsetHeight;
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return [collapsed, toggle, setCollapsed];
}

/** Tiny helper for consumers that just want the storage key. */
export const getStoredSidebarCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
};

// Phase 6n-e — narrowed from 260 to 220 px to match the 3CX/Vodia
// sidebar density. 220 still comfortably fits every nav label in EN
// and ES (verified via icon + label pairs), uses 40 px less of the
// viewport on every page, and gives the center pane more breathing
// room — the operator's daily working surface.
export const SIDEBAR_WIDTH_EXPANDED = 220;
export const SIDEBAR_WIDTH_COLLAPSED = 64;
export const SIDEBAR_BREAKPOINT = SMALL_VP;
